import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Heart, MapPin, Sparkles, Users, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LANDING } from '@/constants/testIds';

const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FDFBF7] paper-texture">
      <section data-testid={LANDING.heroSection} className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 right-20 w-96 h-96 bg-[#6B7A4F] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#8E9E82] rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-6 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 fade-in">
              <div className="inline-block">
                <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#6B7A4F]">
                  A Built for Bookish Experience
                </span>
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light leading-tight text-[#2D2A26]">
                Meet Your
                <span className="block font-bold text-[#6B7A4F]">Bookish Bestie</span>
              </h1>
              <p className="text-lg sm:text-xl text-[#5C5955] leading-relaxed max-w-lg">
                Connect with fellow book lovers based on your reading preferences,
                favorite genres, and location. Build meaningful friendships through
                shared stories.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  data-testid={LANDING.getStartedBtn}
                  onClick={() => navigate('/register')}
                  className="bg-[#6B7A4F] hover:bg-[#57653F] text-white px-8 py-6 rounded-full text-lg font-semibold"
                >
                  Get Started
                </Button>
                <Button
                  data-testid={LANDING.loginLink}
                  onClick={() => navigate('/login')}
                  variant="outline"
                  className="border-2 border-[#6B7A4F] text-[#6B7A4F] hover:bg-[#6B7A4F] hover:text-white px-8 py-6 rounded-full text-lg font-semibold"
                >
                  Sign In
                </Button>
              </div>
            </div>

            <div className="relative fade-in">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[3/4] max-h-[720px] mx-auto">
                <img
                  src="/hero-bookish-final.webp"
                  alt="Reader holding Quicksilver by Callie Hart in a cozy bookstore"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2D2A26]/20 to-transparent pointer-events-none"></div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white/90 backdrop-blur-xl p-6 rounded-2xl shadow-xl border border-white/40">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#E2E6D5] rounded-full flex items-center justify-center">
                    <Heart className="w-6 h-6 text-[#6B7A4F]" />
                  </div>
                  <div>
                    <p className="font-bold text-[#2D2A26]">5,000+</p>
                    <p className="text-sm text-[#5C5955]">Book Lovers Connected</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F4F1EA]/50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl sm:text-5xl font-light text-[#2D2A26]">
              How It <span className="font-bold">Works</span>
            </h2>
            <p className="text-lg text-[#5C5955] max-w-2xl mx-auto">
              Building connections through the love of reading has never been easier
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 stagger-in">
            <div className="glass-card p-8 rounded-2xl space-y-4 card-hover">
              <div className="w-14 h-14 bg-[#E2E6D5] rounded-full flex items-center justify-center">
                <BookOpen className="w-7 h-7 text-[#6B7A4F]" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-semibold text-[#2D2A26]">Build Your Library</h3>
              <p className="text-[#5C5955] leading-relaxed">
                Add books you've read, rate them, and share your favorite genres to
                create your unique reading profile.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl space-y-4 card-hover">
              <div className="w-14 h-14 bg-[#E1D9CD] rounded-full flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-[#8E9E82]" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-semibold text-[#2D2A26]">AI-Powered Matching</h3>
              <p className="text-[#5C5955] leading-relaxed">
                Our smart algorithm analyzes your reading preferences and location to
                suggest your perfect bookish bestie.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl space-y-4 card-hover">
              <div className="w-14 h-14 bg-[#E2E6D5] rounded-full flex items-center justify-center">
                <MessageCircle className="w-7 h-7 text-[#6B7A4F]" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-semibold text-[#2D2A26]">Connect & Chat</h3>
              <p className="text-[#5C5955] leading-relaxed">
                Send connection requests, chat with fellow readers, and plan real-life
                meetups at your local coffee shop or bookstore.
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-12 bg-[#2D2A26] text-white">
        <div className="container mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <BookOpen className="w-6 h-6" />
            <span className="text-xl font-bold">Bookish Bestie</span>
          </div>
          <p className="text-sm opacity-70 mb-2">
            Connecting book lovers, one chapter at a time.
          </p>
          <p className="text-xs opacity-50 tracking-[0.2em] uppercase">
            A Built for Bookish Experience
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
